import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Phone } from 'lucide-react';
import useDocumentMeta from '../hooks/useDocumentMeta';

const LOGO_URL = '/assets/evolvebpm-logo.png';

const sections = [
  { id: 'intro', title: '1. Introduction' },
  { id: 'controller', title: '2. Data Controller & DPO' },
  { id: 'scope', title: '3. Scope & Applicability' },
  { id: 'data-we-collect', title: '4. Personal Data We Collect' },
  { id: 'legal-basis', title: '5. Legal Basis (GDPR)' },
  { id: 'purposes', title: '6. Purposes of Processing' },
  { id: 'canada-casl', title: '7. Canada — CASL & PIPEDA' },
  { id: 'us-laws', title: '8. US — CCPA/CPRA, TCPA & CAN-SPAM' },
  { id: 'other-jurisdictions', title: '9. Other Jurisdictions' },
  { id: 'sharing', title: '10. Data Sharing & Sub-processors' },
  { id: 'transfers', title: '11. International Data Transfers' },
  { id: 'retention', title: '12. Data Retention' },
  { id: 'security', title: '13. Security Measures' },
  { id: 'your-rights', title: '14. Your Rights & Choices' },
  { id: 'cookies', title: '15. Cookies & Tracking' },
  { id: 'children', title: '16. Children’s Privacy' },
  { id: 'changes', title: '17. Changes to This Policy' },
  { id: 'contact', title: '18. How to Contact Us' },
];

export default function PrivacyPolicy() {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.evolvebpm.com';
  useDocumentMeta({
    title: 'Privacy Policy — EvolveBPM',
    description: 'EvolveBPM Privacy Policy covering GDPR (EU/UK), CASL & PIPEDA (Canada), CCPA/CPRA & TCPA (US), and international data transfers.',
    canonical: `${origin}/privacy`,
  });
  return (
    <div className="min-h-screen bg-[#0F1C40] text-[#F1EDE4]">
      {/* Slim nav */}
      <header className="border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="bg-white rounded-lg px-3 py-2 shadow-lg">
              <img src={LOGO_URL} alt="EvolveBPM" className="h-8 w-auto" />
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-[#4ECDC4]">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Sidebar TOC */}
          <aside className="lg:col-span-4">
            <div className="sticky top-24">
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[#4ECDC4] mb-4">
                <Shield className="w-3.5 h-3.5" />
                <span>Privacy Policy</span>
              </div>
              <h1 className="text-3xl lg:text-4xl font-serif italic leading-tight mb-6">
                How EvolveBPM protects your data.
              </h1>
              <div className="text-xs font-mono text-white/50 mb-6">Effective 1 January 2026 · v2026.01</div>
              <ul className="space-y-2 text-sm">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="text-white/60 hover:text-[#4ECDC4] transition-colors">{s.title}</a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-8 space-y-10 text-[15px] leading-relaxed text-white/80">
            <section id="intro">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">1. Introduction</h2>
              <p>EvolveBPM (“EvolveBPM”, “we”, “us”, or “our”) is a global B2B demand generation partner. This Privacy Policy explains what personal data we collect, why we collect it, how we use and share it, and the rights available to you under global data-protection laws — including the EU/UK <strong>GDPR</strong>, Canada&apos;s <strong>CASL</strong> and <strong>PIPEDA</strong>, and the US <strong>CCPA/CPRA</strong>, <strong>TCPA</strong> and <strong>CAN-SPAM Act</strong>.</p>
            </section>

            <section id="controller">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">2. Data Controller & Data Protection Officer</h2>
              <p>EvolveBPM is the data controller for personal data collected via our websites, campaigns and services. Our appointed <strong>Data Protection Officer (DPO)</strong> can be reached at <a className="text-[#4ECDC4] underline" href="mailto:DPO@evolvebpm.com">DPO@evolvebpm.com</a> for any request or complaint regarding your personal data or GDPR-related matters.</p>
            </section>

            <section id="scope">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">3. Scope & Applicability</h2>
              <p>This policy applies to personal data that EvolveBPM processes as (a) a <em>controller</em> — e.g. website visitors, prospects, contacts, and business partners; and (b) a <em>processor</em> — when we deliver demand-generation programs on behalf of our clients under a Data Processing Agreement (DPA).</p>
            </section>

            <section id="data-we-collect">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">4. Personal Data We Collect</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong>Identity & Contact</strong> — name, business email, phone, postal address, job title, employer.</li>
                <li><strong>Business intent signals</strong> — firmographics, technographics, engagement history, content downloads.</li>
                <li><strong>Third-party data</strong> — verified from partners such as Bombora, HG Insights, SalesIntel, Leadiro (aggregated intent, technographic and firmographic signals).</li>
                <li><strong>Device & usage</strong> — IP address, device type, browser, page views, cookie identifiers.</li>
                <li><strong>Campaign engagement</strong> — email opens/clicks, form submissions, call recordings (where consented and permitted by law).</li>
              </ul>
              <p className="mt-3">We do not knowingly collect special-category personal data (health, race, religion, biometrics, etc.).</p>
            </section>

            <section id="legal-basis">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">5. Legal Basis for Processing (GDPR)</h2>
              <p>Under the GDPR/UK GDPR we rely on:</p>
              <ul className="list-disc pl-6 space-y-1.5 mt-2">
                <li><strong>Legitimate interests</strong> — for B2B marketing to relevant professionals in relevant roles, subject to a balancing test.</li>
                <li><strong>Consent</strong> — where required (e.g. non-essential cookies, certain marketing communications, sensitive purposes).</li>
                <li><strong>Contract</strong> — to fulfil our obligations to clients and business partners.</li>
                <li><strong>Legal obligation</strong> — for tax, compliance, and record-keeping requirements.</li>
              </ul>
            </section>

            <section id="purposes">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">6. Purposes of Processing</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li>Delivering B2B demand-generation, ABM, and lead-generation programs on behalf of clients.</li>
                <li>Contacting prospects for relevant business communications, webinars, events, and offers.</li>
                <li>Improving our platforms Predictor.com, Intello and Innovator.</li>
                <li>Fraud prevention, security, analytics, and legal compliance.</li>
              </ul>
            </section>

            <section id="canada-casl">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">7. Canada — CASL & PIPEDA</h2>
              <p>For recipients in Canada, we send Commercial Electronic Messages (CEMs) only where we have <strong>express or implied consent</strong> under Canada&apos;s Anti-Spam Legislation (CASL). Every CEM contains (i) sender identification, (ii) a valid physical address, and (iii) an unsubscribe mechanism that remains functional for at least 60 days. We comply with PIPEDA principles including accountability, purpose limitation, consent, accuracy, safeguards, individual access and challenging compliance.</p>
            </section>

            <section id="us-laws">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">8. United States — CCPA/CPRA, TCPA & CAN-SPAM</h2>
              <ul className="list-disc pl-6 space-y-1.5">
                <li><strong>CCPA/CPRA (California)</strong> — California residents may request access, deletion, correction, and opt-out of “sale”/“sharing” of personal information. We do not sell personal information for cross-context behavioural advertising.</li>
                <li><strong>TCPA</strong> — telemarketing calls and SMS to US numbers are made in accordance with the Telephone Consumer Protection Act, honouring do-not-call preferences and consent requirements.</li>
                <li><strong>CAN-SPAM</strong> — all commercial email includes accurate sender identity, a functional unsubscribe link, and our physical postal address.</li>
              </ul>
            </section>

            <section id="other-jurisdictions">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">9. Other Jurisdictions</h2>
              <p>We honour equivalent obligations under India&apos;s DPDP Act 2023, the UK Data Protection Act 2018, Brazil&apos;s LGPD, Australia&apos;s Privacy Act, Singapore&apos;s PDPA, and other applicable regional laws. Where local law is stricter than this policy, the stricter provision applies.</p>
            </section>

            <section id="sharing">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">10. Data Sharing & Sub-processors</h2>
              <p>We share personal data only with (a) our clients under DPAs, (b) vetted sub-processors under contract (hosting, analytics, telephony, email delivery, intent-data providers), and (c) authorities where legally required. A current list of sub-processors is available on request from <a className="text-[#4ECDC4] underline" href="mailto:DPO@evolvebpm.com">DPO@evolvebpm.com</a>.</p>
            </section>

            <section id="transfers">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">11. International Data Transfers</h2>
              <p>Transfers of personal data outside the EEA, UK or Canada rely on Standard Contractual Clauses (SCCs), UK IDTA/UK Addendum, adequacy decisions, or other lawful transfer mechanisms, and are subject to a transfer impact assessment where required.</p>
            </section>

            <section id="retention">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">12. Data Retention</h2>
              <p>We retain personal data only for as long as necessary for the purposes described, to meet contractual obligations, or to comply with applicable law. Prospect records are reviewed at least annually; unengaged records are suppressed or deleted.</p>
            </section>

            <section id="security">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">13. Security Measures</h2>
              <p>EvolveBPM is a GDPR and <strong>Cyber Essentials</strong> certified organization. We implement industry-standard technical and organisational measures — role-based access control, encryption in transit and at rest, network segmentation, MFA, audit logging, incident response — to protect personal data against unauthorised access, alteration, disclosure or destruction.</p>
            </section>

            <section id="your-rights">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">14. Your Rights & Choices</h2>
              <p>You may have rights of access, rectification, erasure, restriction, portability and objection. You may also withdraw consent at any time or opt-out of marketing communications by clicking unsubscribe in any email or emailing us. To exercise your rights write to <a className="text-[#4ECDC4] underline" href="mailto:DPO@evolvebpm.com">DPO@evolvebpm.com</a>. You have the right to lodge a complaint with your local supervisory authority (e.g. ICO in the UK, CNIL in France, OPC in Canada).</p>
            </section>

            <section id="cookies">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">15. Cookies & Tracking</h2>
              <p>We use strictly necessary cookies to operate the site and, with your consent, analytics/marketing cookies to measure campaign performance. You can manage preferences through the cookie banner or your browser settings.</p>
            </section>

            <section id="children">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">16. Children&apos;s Privacy</h2>
              <p>Our services target business professionals and are not directed to individuals under 16. We do not knowingly collect data from children.</p>
            </section>

            <section id="changes">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-3">17. Changes to This Policy</h2>
              <p>We may update this policy from time to time. Material changes will be communicated on this page with a new effective date. Continued use of our services after changes indicates acceptance of the updated policy.</p>
            </section>

            <section id="contact" className="bg-[#12234C] border border-white/10 rounded-2xl p-6 lg:p-8">
              <h2 className="text-2xl font-serif italic text-[#F1EDE4] mb-4">18. How to Contact Us</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#4ECDC4]" />
                  <span>General: <a className="text-[#4ECDC4] underline" href="mailto:Satish.k@evolvebpm.com">Satish.k@evolvebpm.com</a></span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-[#4ECDC4]" />
                  <span>GDPR / Data Protection Officer: <a className="text-[#4ECDC4] underline" href="mailto:DPO@evolvebpm.com">DPO@evolvebpm.com</a></span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-[#4ECDC4]" />
                  <span>US: <a className="text-[#4ECDC4]" href="tel:+12013796001">+1 201 379 6001</a> · UK: <a className="text-[#4ECDC4]" href="tel:+4474635393">+44 7463 5393</a> · India: <a className="text-[#4ECDC4]" href="tel:+919686596333">+91 9686596333</a></span>
                </div>
              </div>
            </section>

            <div className="pt-8 border-t border-white/10 text-xs text-white/50">
              © 2026 EvolveBPM. All rights reserved. · <span className="italic">Decoding the sales ecosystem.</span>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
}
